import React, { FC, useContext } from 'react';
import { ContextApi } from 'react-uforms';
import { Option, Question, questionFieldType } from '@ch/common/types/data';
import { Box } from '@ch/common/components/box';
import { FormRadio } from '@ch/common/components/form/radio';
import { FormInput } from '@ch/common/components/form/input';
import { Heading } from '@ch/common/components/heading';
import { FormDate } from '@ch/common/components/form/date';
import { customFieldAttr, quizFieldOptionProps, quizFieldRadioProps } from './const';
import QuizFieldPerson from './quiz-field-person';
import QuizFieldDialog from './quiz-field-dialog';
import QuizFieldAutocomplete from './quiz-field-autocomplete';
import QuizFieldWrap from './quiz-field-wrap';
import QuizFieldComplex from './quiz-field-complex';
import QuizFieldDropdown from './quiz-field-dropdown';
import QuizFieldDescription from './quiz-field-description';
import QuizTooltip from '@ch/common/components/quiz-tooltip/quiz-tooltip';
import QuizFieldDuplicator from './quiz-field-duplicator';
import QuizFieldCheckbox from './quiz-field-checkbox';
import QuizFieldDocument from './quiz-field-document';
import { useLang } from '../use-lang';
import QuizFieldManualDialog from './quiz-field-manual-dialog';
import QuizFieldTextarea from './quiz-field-textarea';
import QuizFieldAutocompleteInput from './quiz-field-googlemap-autocomplete';
import { QuizContext } from './quiz-context';
import { useAnalyticsData } from './hooks/use-update-analytics-data';

const QuizFieldInner: FC<Question> = props => {
  const { getErrors, getValue, setValue } = useContext(ContextApi);
  const { lang } = useLang();
  const { identifier: name, field_type: type, options, placeholder, disabled: defaultDisable } = props;
  const customAttr = getValue(`${customFieldAttr}.${name}`);
  const disabled = (customAttr && Object.values(customAttr).some(value => value)) || defaultDisable;
  const { intakeId } = useContext(QuizContext);
  const { updateAnalyticsData } = useAnalyticsData();

  const handlePlaceSelected = place => {
    if (place) {
      setValue(name, place.streetAddress);
      for (const key in place.responseAddress) {
        setValue(key, place.responseAddress[key]);
      }
    }
  };

  switch (type) {
    case questionFieldType.CHECKBOX:
      return <QuizFieldCheckbox {...props} />;
    case questionFieldType.DATE_COMPLETE:
    case questionFieldType.DATE_PARTIAL:
      return (
        <QuizFieldWrap {...props}>
          <FormDate
            {...{
              name,
              id: name,
              $error: !!getErrors(name)?.length,
              disabled,
              locale: lang,
              maxDate: new Date(),
              placeholderText: placeholder,
              isPartialDate: type === questionFieldType.DATE_PARTIAL,
              updateAnalyticsData: () => updateAnalyticsData({ abandonedField: `${name}_FormatOption` }, intakeId),
            }}
          />
        </QuizFieldWrap>
      );
    case questionFieldType.COMPLEX_FIELD:
      return <QuizFieldComplex {...props} />;
    case questionFieldType.DUPLICATOR:
      return <QuizFieldDuplicator {...props} />;
    case questionFieldType.DIALOG_BOX:
      return <QuizFieldDialog {...props} />;
    case questionFieldType.MANUAL_DIALOG_BOX:
      return <QuizFieldManualDialog {...props} />;
    case questionFieldType.DROPDOWN:
    case questionFieldType.INPUT_DROPDOWN:
      return <QuizFieldDropdown {...props} />;
    case questionFieldType.RADIO:
      return (
        <Box $display="flex" $m={[0, 0, 0, 2]}>
          {options?.length > 0 &&
            options.map(({ option: label, identifier: value, tooltip }: Option) => (
              <Box key={value} $m={[0, 15, 0, 0]}>
                <FormRadio {...{ ...quizFieldRadioProps, name, value, disabled }} $size="md">
                  {label}
                  {tooltip && <QuizTooltip id={value}>{tooltip}</QuizTooltip>}
                </FormRadio>
              </Box>
            ))}
        </Box>
      );
    case questionFieldType.RADIO_VERTICAL:
      return (
        <>
          {options?.length > 0 &&
            options.map(({ option: label, identifier: value, tooltip }: Option) => (
              <Box key={value} {...quizFieldOptionProps} $ml={2}>
                <FormRadio {...{ ...quizFieldRadioProps, name, value, disabled }} $size="md">
                  {label}
                  {tooltip && <QuizTooltip id={name}>{tooltip}</QuizTooltip>}
                </FormRadio>
              </Box>
            ))}
        </>
      );
    case questionFieldType.TEXT:
      return (
        <QuizFieldWrap {...props}>
          <FormInput
            {...{
              name,
              placeholder,
              $error: !!getErrors(name)?.length,
              disabled,
            }}
          />
        </QuizFieldWrap>
      );
    case questionFieldType.TEXT_AUTOCOMPLETE:
      return <QuizFieldAutocomplete {...props} />;
    case questionFieldType.TEXTAREA:
      return <QuizFieldTextarea {...props} />;
    case questionFieldType.PERSON_TYPE:
      return <QuizFieldPerson {...props} />;
    case questionFieldType.NOTIFICATION:
      return (
        <Heading $m={[0, 0, 8]} as="h3" $size="h3" $color="primary" $align="center" $fontFamily="aeonikBold">
          {props.question}
        </Heading>
      );
    case questionFieldType.DESCRIPTION_TEXT:
      return <QuizFieldDescription {...props} />;
    case questionFieldType.DOC_UPLOAD:
      return <QuizFieldDocument key={props.identifier} {...props} />;
    case questionFieldType.GOOGLE_AUTOCOMPLETE:
      return <QuizFieldAutocompleteInput {...props} onPlaceSelected={handlePlaceSelected} name={name} />;
    default:
      return null;
  }
};

export default QuizFieldInner;
